// Catalog.ID Client — Chat Account Root pane
// ============================================================================
// The GUI surface for the once-per-ACCOUNT chat-root publication ceremony
// (chat migration §14 M3). All of the logic — the pipeline, the idempotency,
// the refusals — lives in `crypto/chat-root-publication.ts` and is unit-tested
// with no network; this file is the surface, the `ceremony-console-pane.ts`
// split for the same reason.
//
// WHY THIS PANE EXISTS. Verifying a chat message is a chain: envelope signature
// by a per-machine sub-key → the sub-key's cert → the account root's window on
// the `registry_signing_key` claim track (§3). An account that never published
// its root cannot be verified by anybody, so chat is unusable from first
// launch. Until now the only publisher was `apps/cid/dev/chat-cert.ts
// publish-root`, a dev-stack-only path.
//
// NOT THE SAME CEREMONY AS §11 STEP 2(b). That one publishes a per-MACHINE key
// under class `machine`, once per device, driven by the registry module through
// `key-grant/registry-handlers.ts`. This one is per-ACCOUNT, published once,
// and belongs to chat onboarding.
//
// WHY THE FORM IS INLINE RATHER THAN A MODAL. `agents-list-pane` prompts inside
// `shell.showModal`, but the same ceremony has to render at the `/chat-root`
// deep link, where there is no shell at all (the `/ceremony` precedent). One
// inline renderer serves both; a modal would need a second implementation of
// the whole form for the standalone route.
//
// WHY IT ASKS FOR THE SUPERSECRET EVEN WHEN THE BAG IS ALREADY UNLOCKED.
// `chat-cert-handlers.ts` argues, correctly, that a per-request prompt is wrong
// for CERT ISSUANCE: renewal piggybacks on any unlock and a modal every 60 days
// on every device trains users to click through. This is the opposite case —
// once per account, irreversible in practice (the root becomes a public record
// and every future cert chains to it), and started by a deliberate user action
// rather than by a background module. §3 makes the explicit unlock the ceremony;
// an ambient session-long unlock inherited from login is not that. The same
// reasoning already gates agent creation and org rotation.
// ============================================================================

import { createElement, createFieldGroup, createFormRow } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import { createSupersecretTextarea } from '../components/supersecret-textarea';
import { browserChatRootTransport } from '../api/chat-root-transport';
import {
    CHAT_ROOT_KEY_CLASS, ChatRootPublicationError,
    publishChatRoot, readChatRootState,
    type ChatRootState, type ChatRootTransport,
} from '../crypto/chat-root-publication';
import { identity } from '../state/identity';
import { session } from '../state/session';
import { PANE_IDS } from '../shell-config';

/** Active identity's account type, from identity state or the session record
 *  (identity state is empty until login completes / after a reload+unlock). */
function activeAccountType(): string | undefined {
    return identity.getActive()?.accountType ?? session.getInfo()?.accountType;
}

const STATUS_LABELS: Record<ChatRootState['status'], string> = {
    none: 'Not published',
    pending: 'Awaiting operator review',
    published: 'Published',
    rejected: 'Rejected by the operator',
};

const STATUS_COLORS: Record<ChatRootState['status'], string> = {
    none: 'rgba(150,150,150,0.3)',
    pending: 'rgba(100,149,237,0.3)',
    published: 'rgba(80,200,80,0.3)',
    rejected: 'rgba(220,50,50,0.3)',
};

/** Group a hex fingerprint into readable quads — `ceremony-console-pane`'s rule. */
function formatFingerprint(hex: string): string {
    return (hex.match(/.{1,4}/g) ?? [hex]).join(' ');
}

export function createChatRootPane(): PaneType {
    return {
        typeId: PANE_IDS.chatRoot,
        kind: 'leaf',
        defaultTitle: 'Chat Identity',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            const rerender = (): void => {
                const accountType = activeAccountType();
                void renderChatRootCeremony(container, {
                    transport: browserChatRootTransport(),
                    ...(accountType ? { accountType } : {}),
                    onPublished: () => {
                        // Both lists move: a new claim AND a new publication row.
                        ctx.shell.emit('data-changed', { type: 'claims' });
                        ctx.shell.emit('data-changed', { type: 'publicclaims' });
                    },
                });
            };
            rerender();
            ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
                if ((data as { type?: string })?.type === 'publicclaims') rerender();
            }));
            return container;
        },
    };
}

/**
 * Standalone render for the `/chat-root` deep link — the entry point a chat
 * first-run banner sends the user to when it finds no published root
 * (see `apps/chat/dev/.m3-gui-notes.md` §"Trigger contract"). Mirrors
 * `renderCeremonyConsoleStandalone`.
 */
export function renderChatRootStandalone(container: HTMLElement): void {
    container.innerHTML = '';
    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card' });
    card.style.width = '640px';
    card.appendChild(createElement('h2', {
        className: 'cid-auth-title', textContent: 'Chat Identity',
    }));

    const body = createElement('div');
    card.appendChild(body);
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    const accountType = activeAccountType();
    void renderChatRootCeremony(body, {
        transport: browserChatRootTransport(),
        ...(accountType ? { accountType } : {}),
    });
}

export interface ChatRootCeremonyOptions {
    transport: ChatRootTransport;
    /** Called after a successful publication request so a host can refresh. */
    onPublished?: () => void;
    /**
     * Active identity's account type. Only used to say the honest thing to an
     * ORGANIZATION session up front: `ClaimTypes.PublishableBy` for
     * `registry_signing_key` is [Person, Delegate, Agent], so an org publish is
     * a 400 by design — org-capacity roots are deferred as §15 O16. Omitted ⇒
     * the form is offered and the server has the last word.
     */
    accountType?: string;
}

/**
 * Per-container render generation. A publication both re-renders locally AND
 * emits `data-changed`, which re-renders again; two async renders racing on one
 * container would otherwise interleave (each clears, each appends after its own
 * await) and leave the pane showing two publish forms. The later render wins and
 * the earlier one abandons quietly.
 */
const renderTokens = new WeakMap<HTMLElement, number>();

/**
 * Render the whole surface: current state, and the publish form when — and only
 * when — publishing is the right next step. A published or pending root offers
 * no button at all; double-publishing is not something to make easy, and the
 * pipeline refuses it anyway.
 */
export async function renderChatRootCeremony(
    root: HTMLElement,
    opts: ChatRootCeremonyOptions,
): Promise<void> {
    const token = (renderTokens.get(root) ?? 0) + 1;
    renderTokens.set(root, token);
    const superseded = (): boolean => renderTokens.get(root) !== token;

    root.replaceChildren();
    root.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Your chat account root is the key that certifies every device you '
            + 'chat from. Publishing it lets other people verify messages you send — '
            + 'including messages archived years ago. It is published once per account, '
            + 'and it carries no other authority: it cannot sign in, retrieve files, or '
            + 'act as a machine key.',
    }));

    const statusGroup = createFieldGroup({ title: 'Chat account root' });
    const loading = createElement('span', {
        className: 'cid-wizard-status', textContent: 'Loading…',
    });
    statusGroup.body.appendChild(loading);
    root.appendChild(statusGroup.element);

    let state: ChatRootState;
    try {
        state = await readChatRootState(opts.transport);
        if (superseded()) return;
    } catch (err) {
        if (superseded()) return;
        loading.remove();
        statusGroup.body.appendChild(createElement('span', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: 'Could not read your publication status: '
                + (err instanceof Error ? err.message : 'unknown error'),
        }));
        return;
    }
    loading.remove();

    const statusRow = createFormRow({ label: 'Status' });
    const badge = createElement('span', {
        className: 'cid-dashboard-operator-badge',
        textContent: STATUS_LABELS[state.status],
    });
    badge.style.background = STATUS_COLORS[state.status];
    statusRow.content.appendChild(badge);
    statusGroup.body.appendChild(statusRow.element);

    if (state.fingerprint) {
        const fpRow = createFormRow({ label: 'Fingerprint' });
        fpRow.content.appendChild(createElement('span', {
            className: 'cid-profile-cid',
            textContent: formatFingerprint(state.fingerprint),
        }));
        statusGroup.body.appendChild(fpRow.element);
    }
    if (state.publishedAt) {
        const whenRow = createFormRow({ label: 'Published' });
        whenRow.content.appendChild(createElement('span', {
            textContent: new Date(state.publishedAt).toLocaleString(),
        }));
        statusGroup.body.appendChild(whenRow.element);
    }
    if (state.status === 'published') {
        const classRow = createFormRow({ label: 'Key class' });
        classRow.content.appendChild(createElement('span', {
            textContent: `${CHAT_ROOT_KEY_CLASS} — chat verification only`,
        }));
        statusGroup.body.appendChild(classRow.element);
    }
    if (state.status === 'pending') {
        statusGroup.body.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'An operator still has to review this publication. Until they '
                + 'approve it, the root does not resolve on the registry and peers cannot '
                + 'verify your messages yet. Nothing further is needed from you.',
        }));
    }
    if (state.status === 'rejected') {
        const reasonRow = createFormRow({ label: 'Reason' });
        reasonRow.content.appendChild(createElement('span', {
            textContent: state.rejectionReason || '(no reason given)',
        }));
        statusGroup.body.appendChild(reasonRow.element);
    }

    if (state.status === 'published' || state.status === 'pending') return;

    if (opts.accountType === 'organization') {
        const orgGroup = createFieldGroup({ title: 'Not available for organizations' });
        orgGroup.body.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Organizations cannot publish on the signing-key claim track, so '
                + 'an organization cannot publish a chat root yet. Chat in an organization '
                + 'capacity runs under a delegate identity, which can. Publishing '
                + 'organization-capacity roots is a recorded open item.',
        }));
        root.appendChild(orgGroup.element);
        return;
    }

    renderPublishForm(root, opts, state);
}

function renderPublishForm(
    root: HTMLElement,
    opts: ChatRootCeremonyOptions,
    state: ChatRootState,
): void {
    const group = createFieldGroup({
        title: state.status === 'rejected' ? 'Publish again' : 'Publish your chat root',
    });

    group.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'This unlocks your key bag, proves ownership of the chat root to the '
            + 'validator, and sends the publication for operator review. Your key bag is '
            + 'not modified and the root private key never leaves this tab.',
    }));

    const labelRow = createElement('div', { className: 'cid-auth-field' });
    labelRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'Label (shown in your own claims list)',
    }));
    const labelInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
    labelInput.type = 'text';
    labelInput.placeholder = 'Chat account root';
    labelRow.appendChild(labelInput);
    group.body.appendChild(labelRow);

    const ssRow = createElement('div', { className: 'cid-auth-field' });
    ssRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'Supersecret (to unlock your key bag)',
    }));
    const ss = createSupersecretTextarea({ id: 'cid-chat-root-supersecret' });
    ssRow.appendChild(ss.element);
    group.body.appendChild(ssRow);

    const statusEl = createElement('div', { className: 'cid-wizard-status' });
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    group.body.appendChild(statusEl);
    group.body.appendChild(errorEl);

    const submitBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes',
        textContent: 'Publish chat root',
    }) as HTMLButtonElement;
    submitBtn.disabled = true;
    ss.onValidityChange(() => { submitBtn.disabled = !ss.isValid(); });
    group.body.appendChild(submitBtn);

    submitBtn.addEventListener('click', () => {
        if (!ss.isValid()) return;
        void runPublish();
    });

    root.appendChild(group.element);

    async function runPublish(): Promise<void> {
        errorEl.classList.remove('cid-pane-error--visible');
        errorEl.textContent = '';
        submitBtn.disabled = true;
        statusEl.textContent = 'Unlocking key bag and signing the ownership challenge…';
        try {
            const label = labelInput.value.trim();
            const result = await publishChatRoot(opts.transport, {
                supersecret: ss.getValue(),
                ...(label ? { label } : {}),
            });
            const notice = result.alreadyPublished
                ? 'Your chat root was already published — nothing to do.'
                : 'Sent for operator review.'
                    + (result.warning ? ` ${result.warning}` : '');
            statusEl.textContent = notice;
            opts.onPublished?.();
            // Re-render so the state section replaces the form — which also
            // drops the supersecret this form was holding. The re-render wipes
            // `statusEl` with everything else, so the outcome is re-attached
            // afterwards; `result.warning` (no operator yet) is the one thing
            // the state section cannot say on its own.
            await renderChatRootCeremony(root, opts);
            root.prepend(createElement('p', {
                className: 'cid-wizard-status', textContent: notice,
            }));
        } catch (err) {
            submitBtn.disabled = false;
            statusEl.textContent = '';
            errorEl.textContent = err instanceof ChatRootPublicationError
                ? err.message
                : err instanceof Error ? err.message : 'Publication failed';
            errorEl.classList.add('cid-pane-error--visible');
        }
    }
}
